import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { TrendingUp, Users, Clock, CheckSquare, Square, Play, UserCheck } from 'lucide-react';
import ActivityFeed from '@/components/product-manager/ActivityFeed';
import { getActivityLogs, getTasks, getProjects } from '@/services/productManagerService';
import { employeeNamesService } from '@/services/employeeNames';
import { getAllUserAppAccess, AVAILABLE_APPS } from '@/services/appAccess';
import { Task } from '@/types/product-manager';
import { useNavigate } from 'react-router-dom';

interface ActivityStats {
  taskBreakdown: {
    todo: { count: number; projects: string[] };
    inProgress: { count: number; projects: string[] };
    completed: { count: number; projects: string[] };
  };
  authorizedUsers: number;
  mostActiveDay: string;
  projectsCreated: number;
  tasksCompleted: number;
  commentsAdded: number;
  tasksAssigned: number;
  mostActiveProject: string;
  topContributor: string;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<ActivityStats>({
    taskBreakdown: {
      todo: { count: 0, projects: [] },
      inProgress: { count: 0, projects: [] },
      completed: { count: 0, projects: [] }
    },
    authorizedUsers: 0,
    mostActiveDay: 'Today',
    projectsCreated: 0,
    tasksCompleted: 0,
    commentsAdded: 0,
    tasksAssigned: 0,
    mostActiveProject: 'Loading...',
    topContributor: 'Loading...'
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadActivityStats();
  }, []);

  const loadActivityStats = async () => {
    try {
      setLoading(true);
      const [activities, tasks, projects, userAppAccess] = await Promise.all([
        getActivityLogs(),
        getTasks(),
        getProjects(),
        getAllUserAppAccess()
      ]);
      
      // Calculate stats from activities
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      
      const recentActivities = activities.filter(activity => {
        const activityDate = activity.timestamp.toDate ? activity.timestamp.toDate() : new Date(activity.timestamp as any);
        return activityDate >= sevenDaysAgo;
      });
      
      // Count users with Product Manager access
      const authorizedUsers = userAppAccess.filter(user => 
        user.allowedApps.includes(AVAILABLE_APPS.ProductManager)
      ).length;
      
      // Count different types of activities
      const projectsCreated = recentActivities.filter(a => a.entityType === 'project' && a.action === 'created').length;
      const tasksCompleted = recentActivities.filter(a => a.entityType === 'task' && a.action === 'completed').length;
      const commentsAdded = recentActivities.filter(a => a.entityType === 'comment' && a.action === 'created').length;
      const tasksAssigned = recentActivities.filter(a => a.entityType === 'task' && a.action === 'assigned').length;
      
      // Calculate task breakdown
      const todoTasks = tasks.filter(t => t.status === 'todo');
      const inProgressTasks = tasks.filter(t => t.status === 'in-progress');
      const completedTasks = tasks.filter(t => t.status === 'completed');

      // Group tasks by project for breakdown
      const getProjectNames = (taskList: Task[]) => {
        const projectIds = [...new Set(taskList.map(t => t.projectId))];
        return projectIds.map(id => {
          const project = projects.find(p => p.id === id);
          return project?.name || 'Unknown Project';
        });
      };

      const taskBreakdown = {
        todo: { count: todoTasks.length, projects: getProjectNames(todoTasks) },
        inProgress: { count: inProgressTasks.length, projects: getProjectNames(inProgressTasks) },
        completed: { count: completedTasks.length, projects: getProjectNames(completedTasks) }
      };
      
      // Find top contributor based on task assignments
      const assignmentCounts: Record<string, number> = {};
      tasks.forEach(task => {
        if (task.assignedTo) {
          assignmentCounts[task.assignedTo] = (assignmentCounts[task.assignedTo] || 0) + 1;
        }
      });
      
      const topContributorEmail = Object.keys(assignmentCounts).length > 0 ?
        Object.keys(assignmentCounts).reduce((a, b) =>
          assignmentCounts[a] > assignmentCounts[b] ? a : b
        ) : null;
      
      const topContributorName = topContributorEmail ?
        await employeeNamesService.getNameFromEmail(topContributorEmail) || topContributorEmail :
        'No assignments yet';
      
      // Find most active day
      const dayActivities: Record<string, number> = {};
      recentActivities.forEach(activity => {
        const activityDate = activity.timestamp.toDate ? activity.timestamp.toDate() : new Date(activity.timestamp as any);
        const dayKey = activityDate.toDateString();
        dayActivities[dayKey] = (dayActivities[dayKey] || 0) + 1;
      });
      
      const mostActiveDay = Object.keys(dayActivities).length > 0 ? 
        Object.keys(dayActivities).reduce((a, b) =>
          dayActivities[a] > dayActivities[b] ? a : b
        ) : new Date().toDateString();

      // Find most active project
      const projectActivityCounts: Record<string, number> = {};
      recentActivities.forEach(activity => {
        if (activity.metadata?.projectId) {
          const projectId = activity.metadata.projectId;
          projectActivityCounts[projectId] = (projectActivityCounts[projectId] || 0) + 1;
        } else if (activity.entityType === 'project') {
          projectActivityCounts[activity.entityId] = (projectActivityCounts[activity.entityId] || 0) + 1;
        }
      });

      const mostActiveProjectId = Object.keys(projectActivityCounts).length > 0 ?
        Object.keys(projectActivityCounts).reduce((a, b) =>
          projectActivityCounts[a] > projectActivityCounts[b] ? a : b
        ) : null;

      const mostActiveProject = mostActiveProjectId ?
        projects.find(p => p.id === mostActiveProjectId)?.name || 'Unknown Project' :
        'No project activity';
      
      setStats({
        taskBreakdown,
        authorizedUsers,
        mostActiveDay: mostActiveDay === new Date().toDateString() ? 'Today' :
                      new Date(mostActiveDay).toLocaleDateString('en-US', { weekday: 'long' }),
        projectsCreated,
        tasksCompleted,
        commentsAdded,
        tasksAssigned,
        mostActiveProject,
        topContributor: topContributorName
      });
    } catch (error) {
      console.error('Error loading activity stats:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
          <p className="text-gray-600 dark:text-gray-400">
            Track all changes and updates across projects and tasks
          </p>
        </div>
      </div>

      {/* Stats Cards - First Row with 3 widgets */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Task Breakdown Widget */}
        <Card
          className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors shadow-sm hover:shadow-md"
          onClick={() => navigate('/product-manager/tasks')}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Task Breakdown</CardTitle>
            <CheckSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Square className="h-3 w-3 text-gray-500" />
                  <span className="text-sm">To-Do</span>
                </div>
                <span className="text-sm font-medium">
                  {loading ? '...' : stats.taskBreakdown.todo.count}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Play className="h-3 w-3 text-blue-500" />
                  <span className="text-sm">In Progress</span>
                </div>
                <span className="text-sm font-medium">
                  {loading ? '...' : stats.taskBreakdown.inProgress.count}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <CheckSquare className="h-3 w-3 text-green-500" />
                  <span className="text-sm">Completed</span>
                </div>
                <span className="text-sm font-medium">
                  {loading ? '...' : stats.taskBreakdown.completed.count}
                </span>
              </div>
            </div>
            <div className="mt-3 pt-2 border-t border-gray-200 dark:border-gray-700">
              <span className="text-xs text-blue-600 dark:text-blue-400">
                Click to view all tasks →
              </span>
            </div>
          </CardContent>
        </Card>
        
        {/* Team Overview Widget - Combined */}
        <Card className="shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Team Overview</CardTitle>
            <UserCheck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Users className="h-4 w-4 text-blue-500" />
                <span className="text-sm text-gray-600 dark:text-gray-400">Authorized Users</span>
              </div>
              <div className="text-lg font-bold text-blue-600">
                {loading ? '...' : stats.authorizedUsers}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <TrendingUp className="h-4 w-4 text-green-500" />
                <span className="text-sm text-gray-600 dark:text-gray-400">Most Active</span>
              </div>
              <div className="text-lg font-bold text-green-600">
                {loading ? '...' : stats.mostActiveDay}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Activity Summary Widget */}
        <Card className="shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Activity Summary</CardTitle>
            <CardDescription className="text-xs">
              Last 7 days
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-600 dark:text-gray-400">Projects</span>
              <span className="text-sm font-medium">
                {loading ? '...' : stats.projectsCreated}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-600 dark:text-gray-400">Tasks Done</span>
              <span className="text-sm font-medium">
                {loading ? '...' : stats.tasksCompleted}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-600 dark:text-gray-400">Comments</span>
              <span className="text-sm font-medium">
                {loading ? '...' : stats.commentsAdded}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-600 dark:text-gray-400">Assigned</span>
              <span className="text-sm font-medium">
                {loading ? '...' : stats.tasksAssigned}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Activity Feed and Recent Highlights */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-3">
          <ActivityFeed limit={20} />
        </div>
        
        {/* Recent Highlights - Right Column */}
        <div className="space-y-6">
          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center">
                <Clock className="h-4 w-4 mr-2" />
                Recent Highlights
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-sm">
                <div className="font-medium text-gray-900 dark:text-white">Most Active Project</div>
                <div className="text-gray-600 dark:text-gray-400 mt-1">
                  {loading ? 'Loading...' : stats.mostActiveProject}
                </div>
              </div>
              <div className="text-sm">
                <div className="font-medium text-gray-900 dark:text-white">Top Contributor</div>
                <div className="text-gray-600 dark:text-gray-400 mt-1">
                  {loading ? 'Loading...' : stats.topContributor}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;